import { create } from 'zustand';
import type { SurfaceSource } from '@/lib/sync/document-surfaces';
import { useWorkspaceStore } from './use-workspace-store';

export type PendingStage = 'starting' | 'generating' | 'failed' | 'ready' | 'cancelled';

export interface PendingRun {
  runId: string;
  templateId: string;
  templateName: string;
  /** The template's output when the run started, for its Starting row. */
  outputType: 'text' | 'canvas';
  entityId: string;
  knownSurfaceIds: readonly string[];
  sources: readonly SurfaceSource[];
  stage: PendingStage;
  surfaceId: string | null;
  opened: boolean;
}

interface TemplateRunState {
  runs: Record<string, PendingRun>;
  addRun: (run: Omit<PendingRun, 'stage' | 'surfaceId' | 'opened'>) => void;
  matchSurface: (runId: string, surfaceId: string) => void;
  setStage: (runId: string, stage: PendingStage) => void;
  markOpened: (runId: string) => void;
  removeRun: (runId: string) => void;
}

export function pendingStageFor(serverStage: string): PendingStage {
  switch (serverStage) {
    case 'ready':
      return 'ready';
    case 'failed':
    case 'stranded':
      return 'failed';
    case 'cancelled':
      return 'cancelled';
    default:
      return 'generating';
  }
}

export function isTerminal(stage: PendingStage): boolean {
  return stage === 'failed' || stage === 'ready' || stage === 'cancelled';
}

export function runsForEntity(runs: Record<string, PendingRun>, entityId: string): PendingRun[] {
  return Object.values(runs).filter((run) => run.entityId === entityId);
}

export const useTemplateRunStore = create<TemplateRunState>((set) => {
  const patch = (runId: string, change: Partial<PendingRun>) =>
    set((state) => {
      const run = state.runs[runId];
      return run ? { runs: { ...state.runs, [runId]: { ...run, ...change } } } : state;
    });

  return {
    runs: {},
    addRun: (run) =>
      set((state) => ({
        runs: { ...state.runs, [run.runId]: { ...run, stage: 'starting', surfaceId: null, opened: false } },
      })),
    matchSurface: (runId, surfaceId) => patch(runId, { surfaceId }),
    setStage: (runId, stage) => patch(runId, { stage }),
    markOpened: (runId) => patch(runId, { opened: true }),
    removeRun: (runId) =>
      set((state) => {
        const { [runId]: _removed, ...runs } = state.runs;
        return { runs };
      }),
  };
});

// A run is read in its workspace as its requester; after a switch or sign-out
// the server answers 404 and the run would never end. Sign-out sets the active
// workspace to null, so this one subscription covers both.
const unsubWorkspaceReactivity = useWorkspaceStore.subscribe((state, previous) => {
  if (state.activeWorkspaceId === previous.activeWorkspaceId) return;
  useTemplateRunStore.setState({ runs: {} });
});

if (import.meta.hot) {
  import.meta.hot.dispose(() => unsubWorkspaceReactivity());
}
